/**
 * Time Machine #163 — follow the query Lambda's #163 chunk cursor and
 * concatenate EVERY chunk into ONE complete Lambda envelope, so a Time Machine
 * response is never truncated by the 6 MB API Gateway cap — at any office size
 * or history depth.
 *
 * Background: the query Lambda's Time Machine read methods
 * (getStateAndEventsForDate / getStateAtTimestamp / getEventLogByDocumentId /
 * getEventLogForCrew / getEventLogForAttributeCell / getBlackoutDatesAuditByKellerId)
 * pack their big array(s) into <= chunkBudgetBytes slices and return a
 * `cursor` (at `message.result.cursor`) plus `done`. Before this helper the
 * frontend read only the FIRST chunk's arrays and dropped the rest — a silent
 * partial-data bug for any bundle over ~5 MB. This helper makes the frontend
 * loop the cursor to completion and reassemble the full arrays, so downstream
 * parsers (parseStateAndEventsResponse / parseEventLogResponse /
 * parseBlackoutDatesAuditResponse) receive the COMPLETE response exactly as if
 * the Lambda had returned it in one shot.
 *
 * @param {(cursor: object|null) => Promise<object>} fetchChunk
 *   Performs ONE HTTP round-trip for the given cursor (null on the first call)
 *   and returns the raw Lambda envelope:
 *     { statusCode, message: { method, result }, _debugVersion }
 *   The caller's closure is responsible for putting `cursor` into the request
 *   body (e.g. `{ ...body, cursor }`).
 *
 * @param {string[]} arrayFields
 *   The `result` keys whose arrays accumulate across chunks, e.g. ["events"],
 *   ["records"], or ["baselineState", "events"].
 *
 * @param {object} [options]
 * @param {number} [options.maxChunks=100000] Safety backstop against a runaway
 *   cursor loop (a broken Lambda that never sets done). Far above any real
 *   response's chunk count.
 *
 * @returns {Promise<object>} The FIRST chunk's envelope, with its `result`
 *   array fields extended to the full concatenation and `result.cursor`/`done`
 *   normalized to null/true. Non-success envelopes (Lambda runtime error, API
 *   Gateway error string, floor refusal / `result.error`, or a missing/!object
 *   result) are returned verbatim from the first call so the existing parsers
 *   route them exactly as before.
 */
export async function fetchAllLambdaChunks(fetchChunk, arrayFields, options = {}) {
  const maxChunks =
    typeof options.maxChunks === "number" && options.maxChunks > 0
      ? options.maxChunks
      : 100000;

  let firstEnvelope = null;
  let firstResult = null;
  let cursor = null;
  let chunks = 0;

  do {
    const envelope = await fetchChunk(cursor);
    const result =
      envelope && envelope.message && typeof envelope.message === "object"
        ? envelope.message.result
        : undefined;

    // Any non-standard envelope — Lambda runtime error ({errorType/errorMessage}),
    // API Gateway error string (message is a string), floor refusal / validation
    // error (result.error), or a missing/non-object result — is handed back for
    // the parser to classify. On the first call, that's this envelope; on a later
    // call (shouldn't happen — the query is deterministic), fall back to whatever
    // we've already assembled rather than losing it.
    if (
      !result ||
      typeof result !== "object" ||
      result.error
    ) {
      return firstEnvelope || envelope;
    }

    if (firstEnvelope === null) {
      firstEnvelope = envelope;
      firstResult = result;
    } else {
      for (const field of arrayFields) {
        if (Array.isArray(result[field])) {
          firstResult[field] = (firstResult[field] || []).concat(result[field]);
        }
      }
    }

    cursor = result.cursor || null;
    chunks += 1;
  } while (cursor && chunks < maxChunks);

  // Normalize the assembled envelope so it looks like a single complete
  // response to downstream parsers.
  if (firstResult) {
    firstResult.cursor = null;
    firstResult.done = true;
    if (firstResult.meta && typeof firstResult.meta === "object") {
      firstResult.meta.chunksFetched = chunks;
    }
  }
  return firstEnvelope;
}
